import React, { useMemo, useState } from "react";
import { Box, Button, Chip, CircularProgress, Paper, TextField, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BadgeIcon from "@mui/icons-material/Badge";
import CheckIcon from "@mui/icons-material/Check";
import CakeIcon from "@mui/icons-material/Cake";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CustomAvatar from "../../../shared/UIParts/CustomAvatar";
import ContactChildrenRow from "./ContactChildrenRow";
import HouseIllustration from "../../CRM/contacts/HouseIllustration";
import {
  CitizenshipDraft,
  CitizenshipEditor,
  CitizenshipSummary,
  citizenshipFields,
  toCitizenshipDraft,
} from "../../CRM/contacts/CitizenshipCard";
import { Contact } from "../../../interfaces/DTO/crm/contact";
import { MimshakData } from "../../../interfaces/DTO/mimshakData";
import { MimshakContactUtil, AddressInfo } from "../../../services/mimshakContactUtil";
import { formatAge, formatIsoDate, formatName } from "../../../utils/namesFormater";
import { calculateAvatarStyle, AVATAR_COLOR_REV_CLIENT } from "../../../utils/avatarStyleCalculator";

interface ContactAvatarWithDetailsProps {
  contact: Contact;
  /** a couple - an address shared with the spouse is updated for both */
  spouse?: Contact;
  mimshakData: MimshakData[];
  onSave: (contactId: number, fields: Partial<Contact>) => Promise<void>;
  onConfirmIdentification: () => Promise<void>;
  onClearIdentification: () => Promise<void>;
  differentInfo: {
    client_names: string[];
    last_names: string[];
    phones: string[];
    emails: string[];
  };
}

type RowKey = "name" | "phone" | "email" | "id" | "birth" | "address";

interface FieldSpec {
  key: string;
  label: string;
  type?: "text" | "date" | "email" | "tel";
  /** flex share of the row */
  grow?: number;
  ltr?: boolean;
}

const ROW_FIELDS: Record<RowKey, FieldSpec[]> = {
  name: [
    { key: "first_name", label: "שם פרטי" },
    { key: "last_name", label: "שם משפחה" },
  ],
  phone: [{ key: "phone", label: "טלפון נייד", type: "tel", ltr: true }],
  email: [{ key: "email", label: "אימייל", type: "email", ltr: true }],
  // read only - the client's identity (not in edit mode)
  id: [],
  birth: [{ key: "date_of_birth", label: "תאריך לידה", type: "date" }],
  address: [
    { key: "street", label: "רחוב", grow: 3 },
    { key: "house_number", label: "מס׳", grow: 1 },
    { key: "city", label: "עיר", grow: 3 },
  ],
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const rowProblem = (row: RowKey, values: Record<string, string>): string | null => {
  switch (row) {
    case "name":
      return values.first_name?.trim() ? null : "חסר שם פרטי";
    case "email":
      return !values.email?.trim() || EMAIL_PATTERN.test(values.email.trim()) ? null : "אימייל לא תקין";
    case "phone":
      return !values.phone?.trim() || /^[\d\-+ ]{9,15}$/.test(values.phone.trim()) ? null : "טלפון לא תקין";
    case "birth":
      return values.date_of_birth && new Date(values.date_of_birth) > new Date() ? "תאריך בעתיד" : null;
    default:
      return null;
  }
};

const addressOf = (c?: Contact): AddressInfo => ({ street: c?.street || "", house_number: c?.house_number || "", city: c?.city || "" });

export const formatAddress = (a: AddressInfo) =>
  [[a.street, a.house_number].filter(Boolean).join(" "), a.city].filter(Boolean).join(", ");

const sameAddress = (a: AddressInfo, b: AddressInfo) =>
  ["street", "house_number", "city"].every(
    (k) => ((a as Record<string, string | undefined>)[k] ?? "").trim() === ((b as Record<string, string | undefined>)[k] ?? "").trim()
  );

const MissingText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography component="span" color="warning.main" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
    <ErrorOutlineIcon sx={{ fontSize: 18 }} />
    {children}
  </Typography>
);

const ContactAvatarWithDetails: React.FC<ContactAvatarWithDetailsProps> = ({
  contact,
  spouse,
  mimshakData,
  onSave,
  onConfirmIdentification,
  onClearIdentification,
  differentInfo,
}) => {
  /** edit mode - the values of all the editable fields */
  const [editing, setEditing] = useState<Record<string, string> | null>(null);
  const [saving, setSaving] = useState(false);
  /** the citizenship in edit mode - edited with the rest of the card */
  const [citizenship, setCitizenship] = useState<CitizenshipDraft | null>(null);
  const closeEdit = () => {
    setEditing(null);
    setCitizenship(null);
  };
  const [showClearIdentification, setShowClearIdentification] = useState(false);

  const exactAge = formatAge(contact.date_of_birth || "");
  const age = exactAge ? Math.floor(Number(exactAge)).toString() : "";
  const address = addressOf(contact);
  const addressText = formatAddress(address);
  // the spouse lives here too (or has no address yet) - an address change is theirs too
  const spouseSharesAddress = !!spouse?.id && (sameAddress(addressOf(spouse), address) || !spouse.city?.trim());

  const reportedAddresses = useMemo(() => {
    const result: AddressInfo[] = [];
    MimshakContactUtil.getDistinctAddressesByContactId(mimshakData, contact.id!).forEach((a) => {
      if (!formatAddress(a) || sameAddress(a, address) || result.some((r) => sameAddress(r, a))) return;
      result.push(a);
    });
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mimshakData, contact.id, addressText]);

  const EDITABLE_ROWS: RowKey[] = ["name", "phone", "email", "birth", "address"];
  const contactValue = (f: FieldSpec) => {
    const value = (contact as unknown as Record<string, unknown>)[f.key];
    return f.type === "date" ? String(value ?? "").slice(0, 10) : String(value ?? "");
  };

  /** edit mode - every row at once (preset: a value picked from what the funds report) */
  const startEdit = (preset?: Record<string, string>) => {
    const current: Record<string, string> = {};
    EDITABLE_ROWS.forEach((row) => ROW_FIELDS[row].forEach((f) => (current[f.key] = contactValue(f))));
    setEditing({ ...current, ...(editing ?? {}), ...preset });
    if (!editing) setCitizenship(toCitizenshipDraft(contact));
  };

  const problemOf = (row: RowKey) => (editing ? rowProblem(row, editing) : null);
  const firstProblem = editing ? EDITABLE_ROWS.map(problemOf).find(Boolean) ?? null : null;
  const addressChanged = !!editing && ROW_FIELDS.address.some((f) => editing[f.key].trim() !== contactValue(f));

  const save = async () => {
    if (!editing || firstProblem) return;
    // only what changed
    const fields = Object.fromEntries(
      EDITABLE_ROWS.flatMap((row) => ROW_FIELDS[row])
        .filter((f) => editing[f.key].trim() !== contactValue(f))
        .map((f) => [f.key, editing[f.key].trim()])
    ) as Partial<Contact>;
    if (citizenship && JSON.stringify(citizenship) !== JSON.stringify(toCitizenshipDraft(contact))) {
      Object.assign(fields, citizenshipFields(contact, citizenship));
    }
    if (Object.keys(fields).length === 0) {
      closeEdit();
      return;
    }
    setSaving(true);
    try {
      await onSave(contact.id!, fields);
      if (addressChanged && spouseSharesAddress) {
        await onSave(spouse!.id!, Object.fromEntries(ROW_FIELDS.address.map((f) => [f.key, editing[f.key].trim()])) as Partial<Contact>);
      }
      closeEdit();
    } finally {
      setSaving(false);
    }
  };

  const renderFields = (row: RowKey) => (
    <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      {ROW_FIELDS[row].map((f) => (
        <TextField
          key={f.key}
          size="small"
          label={f.label}
          type={f.type ?? "text"}
          value={editing?.[f.key] ?? ""}
          onChange={(e) => setEditing((prev) => (prev ? { ...prev, [f.key]: e.target.value } : prev))}
          onKeyDown={(e) => e.key === "Enter" && save()}
          error={!!problemOf(row)}
          helperText={problemOf(row) ?? undefined}
          InputLabelProps={f.type === "date" ? { shrink: true } : undefined}
          inputProps={f.ltr ? { dir: "ltr" } : undefined}
          sx={{ flex: f.grow ?? 1, minWidth: 0, backgroundColor: "background.paper" }}
        />
      ))}
    </Box>
  );

  const renderRow = (row: RowKey, icon: React.ReactNode, label: string, display: React.ReactNode) => {
    const editable = !!editing && ROW_FIELDS[row].length > 0;
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 1.5,
          py: 0.75,
          minHeight: 52,
          borderRadius: 2,
          backgroundColor: "action.hover",
        }}
      >
        <Box sx={{ color: "text.secondary", display: "flex" }}>{icon}</Box>
        <Typography variant="body2" color="text.secondary" sx={{ width: 84, flexShrink: 0 }}>
          {label}
        </Typography>
        {editable ? (
          renderFields(row)
        ) : (
          <Box sx={{ flex: 1, minWidth: 0, fontSize: "1.05rem", fontWeight: 500 }}>{display}</Box>
        )}
      </Box>
    );
  };

  const hasDifferences =
    differentInfo.client_names.length > 0 ||
    differentInfo.last_names.length > 0 ||
    differentInfo.phones.length > 0 ||
    differentInfo.emails.length > 0 ||
    reportedAddresses.length > 0;

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Paper
        variant="outlined"
        sx={{ p: 2.5, borderRadius: 3, position: "relative", borderColor: editing ? "primary.main" : undefined, borderWidth: editing ? 2 : 1 }}
      >
        {/* like EditableFieldsBlock's "ערוך" - a quiet text button */}
        {!editing && (
          <Button size="small" onClick={() => startEdit()} sx={{ position: "absolute", top: 8, left: 8 }}>
            ערוך
          </Button>
        )}
        {/* header - avatar, name, age */}
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, mb: 2 }}>
          <CustomAvatar
            item={contact}
            isUser={true}
            userColor={calculateAvatarStyle(contact, true).backgroundColor}
            familyColor=""
            sx={{ width: 96, height: 96, fontSize: "2.25rem" }}
          />
          {contact.rev_client === true && (
            <Chip
              label="לקוח REV&U"
              size="small"
              sx={{ backgroundColor: AVATAR_COLOR_REV_CLIENT, color: "#fff", fontWeight: 700, mt: 0.5 }}
            />
          )}
          {editing ? (
            <Box sx={{ width: "100%", mt: 1 }}>{renderFields("name")}</Box>
          ) : (
            <Typography variant="h5" fontWeight={600} textAlign="center" sx={{ mt: 1 }}>
              {formatName(contact) || "ללא שם"}
            </Typography>
          )}
          <Typography variant="h6" fontWeight={600} color={age ? "text.secondary" : "warning.main"}>
            {age ? `גיל ${age}` : "גיל לא ידוע"}
          </Typography>
        </Box>

        {/* details - all edited together in place (the עריכה button) */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <ContactChildrenRow contact={contact} spouse={spouse} mimshakData={mimshakData} onSave={onSave} canAdd={!!editing} />
          {renderRow(
            "phone",
            <PhoneIphoneIcon fontSize="small" />,
            "טלפון",
            contact.phone ? <span dir="ltr">{contact.phone}</span> : <MissingText>לא הוזן</MissingText>
          )}
          {renderRow(
            "email",
            <EmailIcon fontSize="small" />,
            "אימייל",
            contact.email ? (
              <span dir="ltr" style={{ wordBreak: "break-all" }}>{contact.email}</span>
            ) : (
              <MissingText>חסר אימייל</MissingText>
            )
          )}
          {renderRow("id", <BadgeIcon fontSize="small" />, "ת.ז", contact.israeli_id || <MissingText>לא הוזן</MissingText>)}
          {renderRow(
            "birth",
            <CakeIcon fontSize="small" />,
            "תאריך לידה",
            contact.date_of_birth && age ? formatIsoDate(contact.date_of_birth) : <MissingText>חסר תאריך לידה</MissingText>
          )}
          {/* citizenship - edited with the rest of the card */}
          <Box sx={{ px: 1.5, py: 1, borderRadius: 2, backgroundColor: "action.hover" }}>
            {editing && citizenship ? (
              <CitizenshipEditor value={citizenship} onChange={setCitizenship} />
            ) : (
              <CitizenshipSummary contact={contact} />
            )}
          </Box>
          {/* the address - with the house drawing */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, px: 1.5, py: 1.5, borderRadius: 2, backgroundColor: "action.hover" }}>
            <HouseIllustration />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                כתובת מגורים
              </Typography>
              {editing ? (
                renderFields("address")
              ) : (
                <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.4 }}>
                  {addressText || <MissingText>חסרה כתובת</MissingText>}
                </Typography>
              )}
            </Box>
          </Box>
          {addressChanged && spouseSharesAddress && (
            <Typography variant="caption" color="text.secondary" sx={{ px: 1.5 }}>
              הכתובת תעודכן גם ל{formatName(spouse!)}
            </Typography>
          )}
        </Box>

        {editing && (
          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <Button
              variant="contained"
              size="large"
              onClick={save}
              disabled={saving || !!firstProblem}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckIcon sx={{ ml: 0.5 }} />}
            >
              שמור
            </Button>
            <Button variant="outlined" size="large" onClick={closeEdit} disabled={saving}>
              חזור
            </Button>
          </Box>
        )}

        {/* identification */}
        <Box sx={{ mt: 2 }}>
          {contact.data?.identification_time_stamp ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
              <CheckCircleIcon color="success" fontSize="small" />
              <Typography
                variant="body2"
                color="success.dark"
                sx={{ cursor: "pointer" }}
                onClick={() => setShowClearIdentification((v) => !v)}
              >
                תעודה מזהה אומתה ב-
                {new Date(contact.data.identification_time_stamp).toLocaleDateString("he-IL", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </Typography>
              {showClearIdentification && (
                <Button
                  size="small"
                  color="error"
                  onClick={() => {
                    onClearIdentification();
                    setShowClearIdentification(false);
                  }}
                >
                  מחק אימות
                </Button>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                px: 1.5,
                py: 1,
                border: "2px dashed",
                borderColor: "primary.main",
                borderRadius: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <BadgeIcon color="primary" />
                <Typography variant="body2" fontWeight={600}>
                  בדוק תעודה מזהה
                </Typography>
              </Box>
              <Button variant="contained" size="small" onClick={onConfirmIdentification} startIcon={<CheckCircleIcon sx={{ ml: 1 }} />}>
                אשר זיהוי
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      {/* what the funds report differently - click to use it */}
      {hasDifferences && (
        <Paper
          variant="outlined"
          sx={{ p: 1.5, borderRadius: 3, borderColor: "warning.light", backgroundColor: "rgba(237, 108, 2, 0.04)" }}
        >
          <Typography variant="body2" fontWeight={700} color="warning.dark" sx={{ mb: 1 }}>
            פרטים אחרים שדווחו בקופות (לחץ לעדכון)
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {differentInfo.client_names.length > 0 && (
              <DifferenceLine label="שמות">
                {differentInfo.client_names.map((n) => (
                  <Chip key={n} size="small" variant="outlined" label={n} />
                ))}
              </DifferenceLine>
            )}
            {differentInfo.last_names.length > 0 && (
              <DifferenceLine label="שמות משפחה">
                {differentInfo.last_names.map((n) => (
                  <Chip key={n} size="small" variant="outlined" label={n} onClick={() => startEdit({ last_name: n })} />
                ))}
              </DifferenceLine>
            )}
            {differentInfo.phones.length > 0 && (
              <DifferenceLine label="טלפונים">
                {differentInfo.phones.map((p) => (
                  <Chip key={p} size="small" variant="outlined" label={<span dir="ltr">{p}</span>} onClick={() => startEdit({ phone: p })} />
                ))}
              </DifferenceLine>
            )}
            {differentInfo.emails.length > 0 && (
              <DifferenceLine label="מיילים">
                {differentInfo.emails.map((m) => (
                  <Chip key={m} size="small" variant="outlined" label={<span dir="ltr">{m}</span>} onClick={() => startEdit({ email: m })} />
                ))}
              </DifferenceLine>
            )}
            {reportedAddresses.length > 0 && (
              <DifferenceLine label="כתובות">
                {reportedAddresses.map((a, i) => (
                  <Chip
                    key={i}
                    size="small"
                    variant="outlined"
                    label={formatAddress(a)}
                    onClick={() =>
                      startEdit({ street: a.street ?? "", house_number: a.house_number ?? "", city: a.city ?? "" })
                    }
                  />
                ))}
              </DifferenceLine>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

const DifferenceLine: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
    <Typography variant="caption" color="text.secondary" sx={{ width: 76, flexShrink: 0, pt: 0.5 }}>
      {label}
    </Typography>
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>{children}</Box>
  </Box>
);

export default ContactAvatarWithDetails;
